import { useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import { Send, X, Loader2, UserPlus, Check } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useApi } from '../api/client';
import { useCatalogs } from '../api/useCatalogs';
import type { Contact, Outreach } from '../types/api';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (outreach: Outreach) => void;
  // Optional preselected outreach kind (e.g., 'initial' or 'follow_up').
  // Passed by the inline +-buttons on each Dashboard tile.
  defaultKindShortName?: 'initial' | 'follow_up';
}

interface QuickAddResponse {
  outreach: Outreach;
  contact_was_created: boolean;
}

export default function QuickLogModal({
  open,
  onClose,
  onSuccess,
  defaultKindShortName,
}: Props) {
  const apiFetch = useApi();
  const { catalogs, error: catalogsError } = useCatalogs();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactQuery, setContactQuery] = useState('');
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const [statusId, setStatusId] = useState<number | ''>('');
  const [kindId, setKindId] = useState<number | ''>('');
  const [statusOnly, setStatusOnly] = useState(false);
  const [methodId, setMethodId] = useState<number | ''>('');
  const [warmthId, setWarmthId] = useState<number | ''>('');
  const [platformId, setPlatformId] = useState<number | ''>('');
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [templateCopied, setTemplateCopied] = useState(false);
  const [sentAtDate, setSentAtDate] = useState<string>(localToday());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showContactList, setShowContactList] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  // Search contacts server-side as she types (debounced), so the picker works
  // across the full address book, not just the first page.
  useEffect(() => {
    if (!open || selectedContactId != null) return;
    let cancelled = false;
    const t = setTimeout(() => {
      const params = new URLSearchParams({ size: '8' });
      const q = contactQuery.trim();
      if (q) params.set('q', q);
      apiFetch(`/contacts?${params.toString()}`)
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((d: { contacts: Contact[] }) => {
          if (!cancelled) setContacts(d.contacts);
        })
        .catch((e) => {
          if (!cancelled) setError(String(e));
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, contactQuery, selectedContactId, apiFetch]);

  // Apply catalog defaults once they arrive. The kind preselects whatever
  // the caller passed (initial / follow_up / fallback to 'initial').
  useEffect(() => {
    if (!catalogs) return;
    const desiredKind = defaultKindShortName ?? 'initial';
    const kind = catalogs.outreach_kinds.find((k) => k.short_name === desiredKind);
    if (kind) setKindId(kind.id);
    if (statusId === '') {
      const sent = catalogs.outreach_statuses.find((s) => s.short_name === 'sent');
      if (sent) setStatusId(sent.id);
    }
  }, [catalogs, defaultKindShortName, statusId]);

  // Focus the contact field on open.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Esc closes, Cmd/Ctrl-Enter submits.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  function reset() {
    setContactQuery('');
    setSelectedContactId(null);
    setMethodId('');
    setWarmthId('');
    setPlatformId('');
    setTemplateId('');
    setTemplateCopied(false);
    setSentAtDate(localToday());
    setStatusOnly(false);
    setError(null);
    setShowContactList(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function pickContact(c: Contact) {
    setSelectedContactId(c.id);
    setContactQuery(c.name);
    setShowContactList(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    setError(null);

    const trimmedName = contactQuery.trim();
    if (!selectedContactId && !trimmedName) {
      setError('Pick or type a contact name.');
      return;
    }
    if (statusId === '') {
      setError('Status is required.');
      return;
    }
    if (!statusOnly && kindId === '') {
      setError('Kind is required.');
      return;
    }

    const body: Record<string, unknown> = {
      status_id: statusId,
    };
    if (statusOnly) {
      body.status_only = true;
    } else {
      body.outreach_kind_id = kindId;
    }
    if (selectedContactId) {
      body.contact_id = selectedContactId;
    } else {
      body.contact_name = trimmedName;
      if (platformId !== '') body.platform_id = platformId;
    }
    if (methodId !== '') body.contact_method_id = methodId;
    if (warmthId !== '') body.warmth_tier_id = warmthId;
    if (templateId !== '') body.message_template_id = templateId;
    if (sentAtDate && sentAtDate !== localToday()) {
      // Only send if user changed it from today's default; else let the
      // server stamp CURRENT_TIMESTAMP for sub-second precision.
      body.sent_at = sentAtDate;
    }

    setSaving(true);
    try {
      const r = await apiFetch('/outreaches/quick', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d: QuickAddResponse = await r.json();
      // Tiny celebratory pop on every successful log (separate from the
      // big confetti burst on hitting a target).
      confetti({
        particleCount: 30,
        spread: 50,
        startVelocity: 25,
        origin: { y: 0.7 },
        colors: ['#A1CD3C', '#80C99B', '#9CD9DE'],
      });
      onSuccess(d.outreach);
      reset();
      onClose();
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  // Server already filtered + limited the list (see the search effect above).
  const filteredContacts = contacts;

  const isNewContact = selectedContactId == null && contactQuery.trim().length > 0;

  if (!open) return null;
  return (
    <Modal onClose={handleClose}>
            <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-brand-lime to-brand-mint px-5 py-3 text-brand-forest">
              <div className="flex items-center gap-2 font-display font-black">
                <Send className="h-5 w-5" />
                Quick log
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="rounded-md p-1 transition hover:bg-brand-forest/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 px-5 py-5">
              <div className="relative">
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-sage">
                  Contact
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  value={contactQuery}
                  onChange={(e) => {
                    setContactQuery(e.target.value);
                    setSelectedContactId(null);
                    setShowContactList(true);
                  }}
                  onFocus={() => setShowContactList(true)}
                  onBlur={() => setTimeout(() => setShowContactList(false), 120)}
                  placeholder="Type a name…"
                  className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                />
                {isNewContact && (
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-brand-coral-600">
                    <UserPlus className="h-3.5 w-3.5" />
                    New contact will be created
                  </div>
                )}
                {selectedContactId != null && (
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-brand-lime-700">
                    <Check className="h-3.5 w-3.5" />
                    Existing contact selected
                  </div>
                )}
                {showContactList && filteredContacts.length > 0 && (
                  <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-brand-sage/30 bg-white shadow-lg">
                    {filteredContacts.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            pickContact(c);
                          }}
                          className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-brand-forest transition hover:bg-brand-mint-100"
                        >
                          <span>{c.name}</span>
                          {c.last_touched_at && (
                            <span className="text-xs text-brand-sage">
                              last touched{' '}
                              {new Date(c.last_touched_at).toLocaleDateString()}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-sage">
                  When
                </label>
                <input
                  type="date"
                  value={sentAtDate}
                  max={localToday()}
                  onChange={(e) => setSentAtDate(e.target.value)}
                  className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                />
              </div>

              {isNewContact && catalogs && (
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-sage">
                    Platform (where she lives)
                  </label>
                  <select
                    value={platformId}
                    onChange={(e) =>
                      setPlatformId(e.target.value === '' ? '' : Number(e.target.value))
                    }
                    className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                  >
                    <option value="">—</option>
                    {catalogs.platforms.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.description}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {catalogs && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-sage">
                      Log type
                    </label>
                    <select
                      value={statusOnly ? 'status_only' : kindId}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === 'status_only') {
                          setStatusOnly(true);
                        } else {
                          setStatusOnly(false);
                          setKindId(v === '' ? '' : Number(v));
                        }
                      }}
                      className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                    >
                      {catalogs.outreach_kinds.map((k) => (
                        <option key={k.id} value={k.id}>
                          {k.description}
                        </option>
                      ))}
                      <option value="status_only">Status change only</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-sage">
                      Status
                    </label>
                    <select
                      value={statusId}
                      onChange={(e) =>
                        setStatusId(e.target.value === '' ? '' : Number(e.target.value))
                      }
                      className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                    >
                      {catalogs.outreach_statuses.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-sage">
                      Method
                    </label>
                    <select
                      value={methodId}
                      onChange={(e) =>
                        setMethodId(e.target.value === '' ? '' : Number(e.target.value))
                      }
                      className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                    >
                      <option value="">—</option>
                      {catalogs.contact_methods.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-sage">
                      Warmth
                    </label>
                    <select
                      value={warmthId}
                      onChange={(e) =>
                        setWarmthId(e.target.value === '' ? '' : Number(e.target.value))
                      }
                      className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                    >
                      <option value="">—</option>
                      {catalogs.warmth_tiers.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {catalogs && catalogs.message_templates.length > 0 && (
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className="text-xs font-bold uppercase tracking-wider text-brand-sage">
                      Message template
                    </label>
                    {templateId !== '' && (
                      <button
                        type="button"
                        onClick={async () => {
                          const t = catalogs.message_templates.find(
                            (x) => x.id === templateId,
                          );
                          if (!t) return;
                          const firstName = contactQuery.trim().split(/\s+/)[0];
                          const body = firstName
                            ? t.body.replace(/\[name\]/gi, firstName)
                            : t.body;
                          try {
                            await navigator.clipboard.writeText(body);
                            setTemplateCopied(true);
                            setTimeout(() => setTemplateCopied(false), 1500);
                          } catch {
                            // fall through silently on older browsers
                          }
                        }}
                        className={[
                          'rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition',
                          templateCopied
                            ? 'bg-brand-lime-700 text-white'
                            : 'bg-brand-lime text-brand-forest hover:bg-brand-lime-600',
                        ].join(' ')}
                      >
                        {templateCopied ? '✓ Copied' : 'Copy body'}
                      </button>
                    )}
                  </div>
                  <select
                    value={templateId}
                    onChange={(e) => {
                      setTemplateId(
                        e.target.value === '' ? '' : Number(e.target.value),
                      );
                      setTemplateCopied(false);
                    }}
                    className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                  >
                    <option value="">— none —</option>
                    {catalogs.message_template_kinds
                      .filter((k) =>
                        catalogs.message_templates.some(
                          (t) => t.message_template_kind_id === k.id,
                        ),
                      )
                      .map((k) => (
                        <optgroup key={k.id} label={k.description}>
                          {catalogs.message_templates
                            .filter((t) => t.message_template_kind_id === k.id)
                            .map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                                {t.is_shared ? '' : ' (yours)'}
                              </option>
                            ))}
                        </optgroup>
                      ))}
                  </select>
                </div>
              )}

              {(error || catalogsError) && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error || catalogsError}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 border-t border-brand-sage/20 pt-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-md px-4 py-2 text-sm text-brand-forest hover:bg-brand-sage/10"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !catalogs}
                  className="flex items-center gap-2 rounded-xl bg-brand-lime px-4 py-2 font-display font-black text-brand-forest shadow-sm transition hover:bg-brand-lime-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {saving ? 'Logging…' : 'Log it'}
                </button>
              </div>
            </form>
    </Modal>
  );
}

// Local YYYY-MM-DD for prefilling the date picker. Native <input type="date">
// expects local-zone date strings, not UTC, so we build it from getFullYear /
// getMonth / getDate rather than toISOString().
function localToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}